import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStudentView } from '../contexts/StudentViewContext';
import { authFetch, authPost, authDelete, apiFetch } from '../utils/api';
import { gradeClass, gradeColor, bgColor, gradeHex, pctGrade } from '../utils/grades';
import { formatDate, timeAgo, parseUTC, formatFlightTime, formatDetailTime } from '../utils/dates';
import { distNm, crossTrackFt, bearing, deriveSkillIcons, renderSkillIcon } from '../utils/geo';
import { DataQualityBadge } from '../components/shared/DataQualityBadge';
import { SectionHeader } from '../components/shared/SectionHeader';
import { ScoreRing } from '../components/charts/ScoreRing';
import { VerticalProfile } from '../components/charts/VerticalProfile';
import { HorizontalTrack } from '../components/charts/HorizontalTrack';
import { FleetDistribution } from '../components/charts/FleetDistribution';

function HomePage() {
  const navigate = useNavigate();
  const { user, token, settings, initFilters } = useAuth();
  const [query, setQuery] = useState('');
  
  const _flightDisplayMode = () => { try { return localStorage.getItem('bmac3_flight_display') || 'sessions'; } catch { return 'sessions'; } };
  const _sf = (() => { try { return JSON.parse(localStorage.getItem('bmac3_flt_filters') || 'null'); } catch { return null; } })();
  const [feedMode, setFeedMode] = useState(_sf?.feedMode ?? 'my_ac');
  const _savef = (u) => { try { const s = JSON.parse(localStorage.getItem('bmac3_flt_filters') || '{}'); localStorage.setItem('bmac3_flt_filters', JSON.stringify({...s, ...u})); } catch {} };
  const [maskedFeed, setMaskedFeed] = useState(settings?.masked_feed ?? false);
  const [results, setResults] = useState(null);
  const [filters, setFilters] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const debounceRef = useRef(null);
  const location = useLocation();

  // Deep-link from the dashboard "Practical" tile opens the feed in My Flights
  // mode (proficiency = claimed flights). Fires once per navigation.
  useEffect(() => {
    const fm = location.state && location.state.feedMode;
    if (fm) setFeedMode(prev => (prev !== fm ? fm : prev));
  }, [location.state]);
  const [lastRefresh, setLastRefresh] = useState(null);
  const lastRefreshRef = useRef(null);
  const STALE_MS = 30000; // skip auto-refresh if <30s since last
  const [showFeedExplainer, setShowFeedExplainer] = useState(false);

  // Load masked feed setting from cached auth context
  useEffect(() => {
    setMaskedFeed(settings.masked_feed || false);
  }, [settings.masked_feed]);



  const buildParams = (extra = {}) => {
    const params = new URLSearchParams();
    if (query) params.set('callsign', query.toUpperCase());
    params.set('limit', '50');
    if (feedMode === 'my_ac') params.set('my_ac', 'true');
    if (feedMode === 'my_flights') params.set('my_flights', 'true');
    if (maskedFeed) params.set('masked_feed', 'true');
    Object.entries(extra).forEach(([k, v]) => params.set(k, String(v)));
    return params;
  };

  const doSearch = (append = false, offset = 0) => {
    if (!append) setLoading(true);
    else setLoadingMore(true);
    const params = buildParams({ offset });
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 15000);
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`https://scores.aprmode.com/api/search?${params}`, { headers, signal: controller.signal })
      .then(res => { if (!res.ok) throw new Error(`API error ${res.status}`); return res.json(); })
      .then(data => {
        clearTimeout(timeoutId);
        if (append) setResults(prev => [...(prev || []), ...data.flights]);
        else { setResults(data.flights); const now = new Date(); setLastRefresh(now); lastRefreshRef.current = now; }
        setHasMore(data.has_more || false);
        setLoading(false); setLoadingMore(false);
      }).catch(() => { clearTimeout(timeoutId); setLoading(false); setLoadingMore(false); });
  };

  // Event-based refresh: only if data is stale (>30s old)
  const refreshIfStale = useCallback(() => {
    const last = lastRefreshRef.current;
    if (!last || (Date.now() - last.getTime()) > STALE_MS) {
      doSearch();
    }
  }, [maskedFeed, feedMode, token, query]);

  // Visibility change (tab switch back), online reconnect
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refreshIfStale(); };
    const onOnline = () => refreshIfStale();
    const onFocus = () => refreshIfStale();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onOnline);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshIfStale]);

  // In-SPA return to /flights doesn't fire visibility/focus, so refetch here.
  // A score on /vfr sets aprFeedDirty -> force a fresh fetch past the 30s throttle.
  const feedNavFirst = useRef(true);
  useEffect(() => {
    if (feedNavFirst.current) { feedNavFirst.current = false; return; }
    if (location.pathname !== '/flights') return;
    if (sessionStorage.getItem('aprFeedDirty')) {
      sessionStorage.removeItem('aprFeedDirty');
      doSearch();
    } else {
      refreshIfStale();
    }
    // eslint-disable-next-line
  }, [location.pathname]);

  const loadMore = () => {
    if (!results) return;
    doSearch(true, results.length);
  };

  // Consume filters pre-fetched by AuthContext /api/init — no extra round trip
  useEffect(() => {
    if (initFilters) setFilters(initFilters);
  }, [initFilters]);

  // Initial load + filter/auth changes — fire immediately. No `query` dep.
  useEffect(() => {
    doSearch();
    // eslint-disable-next-line
  }, [feedMode, maskedFeed, token]);

  // Search-box typing — debounced. Skips first render so we don't duplicate
  // the initial load (which would cause loading=true to flash 400ms in).
  const queryFirstRender = useRef(true);
  useEffect(() => {
    if (queryFirstRender.current) { queryFirstRender.current = false; return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      doSearch();
    }, 400);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line
  }, [query]);


  return (
    <>
      {token && (
        <div style={{ display: 'flex', gap: 8, padding: '0 16px 12px' }}>
          {[
            { key: 'my_ac',     label: '\u2708\u00a0My Aircraft' },
            { key: 'my_flights', label: '\u2713\u00a0My Flights' },
            { key: 'type_feed', label: '\u229e\u00a0Type Feed' },
          ].map(({ key, label }) => {
            const active = feedMode === key;
            const three = true;
            return (
              <button key={key} onClick={() => { setFeedMode(key); _savef({ feedMode: key }); }}
                style={{
                  flex: 1, padding: three ? '10px 6px' : '11px 16px', borderRadius: 22,
                  fontSize: three ? 13 : 15, fontWeight: 600,
                  cursor: 'pointer', transition: 'all 0.18s',
                  whiteSpace: 'nowrap', textAlign: 'center',
                  background: active ? 'var(--accent)' : 'var(--bg-card)',
                  color: active ? '#fff' : 'var(--text-secondary)',
                  border: active ? '1.5px solid var(--accent)' : '1px solid var(--border)',
                  boxShadow: active ? '0 2px 8px rgba(59,130,246,0.28)' : 'none',
                }}>
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* View explanation — sits between filter pills and search bar (above results) */}
      {!loading && !query && (
        <div style={{ padding: '2px 16px 12px', fontSize: 15, color: 'var(--page-bg-sub)', lineHeight: 1.55 }}>
          {!token
            ? 'Sign in to see scored approaches for aircraft you fly. Showing a sample of recent IFR arrivals (tail numbers hidden).'
            : feedMode === 'my_ac'
              ? 'Showing scored approaches for aircraft you\'ve claimed in your profile. Add tail numbers in Settings → My Aircraft.'
              : feedMode === 'my_flights'
                ? 'Showing only the individual flights you\'ve claimed as your own — the basis for your proficiency tracking.'
                : 'Showing scored approaches for your aircraft types based on your pilot profile.'}
          {' '}
          <button type="button" onClick={() => setShowFeedExplainer(true)}
            style={{ background: 'none', border: 'none', padding: 0, color: '#60a5fa', fontSize: 15, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
            More
          </button>
        </div>
      )}

      <div className="search-section">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1 }}>
            <span className="search-icon">⌕</span>
            <input type="text" placeholder="Search callsign, airport, type..." value={query}
              onChange={(e) => setQuery(e.target.value)} autoComplete="off" autoCorrect="off" spellCheck="false"
              style={query ? { paddingRight: 44 } : undefined} />
            {query && (
              <button type="button" aria-label="Clear search" onClick={() => setQuery('')}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 24, fontWeight: 600, lineHeight: 1, padding: 0 }}>×</button>
            )}
          </div>

        </div>
      {lastRefresh && (() => {
        const ageMs = Date.now() - lastRefresh.getTime();
        const ageSec = Math.floor(ageMs / 1000);
        const ageColor = ageSec < 30 ? '#10b981' : ageSec < 120 ? '#eab308' : '#ef4444';
        const ageBg = ageSec < 30 ? 'rgba(16,185,129,0.08)' : ageSec < 120 ? 'rgba(234,179,8,0.08)' : 'rgba(239,68,68,0.08)';
        const ageBorder = ageSec < 30 ? 'rgba(16,185,129,0.3)' : ageSec < 120 ? 'rgba(234,179,8,0.3)' : 'rgba(239,68,68,0.3)';
        return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '6px 0 0' }}>
          <button onClick={() => doSearch()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
              borderRadius: 20, border: `1px solid ${ageBorder}`, background: ageBg,
              color: ageColor, fontSize: 13, cursor: 'pointer', transition: 'all 0.2s' }}>
            <span style={{ fontSize: 10, lineHeight: 1 }}>{'●'}</span>
            <span>{lastRefresh.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
          </button>
        </div>
        );
      })()}
      </div>



      {/* filter-toggles removed */}

      {loading && <div className="loading"><div className="spinner" /><span>Loading...</span></div>}

      {!loading && results && results.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">{'🛬'}</div>
          <p>No approaches found{query ? ` for "${query}"` : ''}</p>
          {query && (
            <button onClick={() => navigate('/vfr', { state: { prefillTail: query.toUpperCase() } })}
              style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', padding: '12px 20px',
                background: 'var(--accent)', border: 'none',
                borderRadius: 24, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                letterSpacing: 0.2, boxShadow: '0 2px 12px rgba(59,130,246,0.35)' }}>
              {'✈️'} Didn't find your flight? Retrieve it here
            </button>
          )}
        </div>
      )}

      {!loading && results && results.length > 0 && (
        <div className="results-list">
          {query && (
            <button onClick={() => navigate('/vfr', { state: { prefillTail: query.toUpperCase() } })}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', padding: '12px 20px', marginBottom: 10,
                background: 'var(--accent)', border: 'none',
                borderRadius: 24, color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
                letterSpacing: 0.2, boxShadow: '0 2px 12px rgba(59,130,246,0.35)' }}>
              ✈️ Didn&apos;t find your flight? Retrieve it here
            </button>
          )}
          {results.map((f) => {
            const skillIcons = deriveSkillIcons(f);
            const isVfr = f.source !== 'swim';
            return (
            <div key={f.gufi || f.id}>
            <div className="score-card"
              style={f.data_quality === 'GO_AROUND' ? { borderLeft: '4px solid #10b981' } : f.data_quality === 'INSUFFICIENT_DATA' ? { borderLeft: '4px solid #9ca3af' } : {}}
              onClick={() => { if (!f.pending) navigate(`/flight/${f.id || encodeURIComponent(f.gufi)}`); }}>
              {f.pending ? (
                <div className="grade-badge" style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: '0.9rem' }}>
                  ⏳
                </div>
              ) : f.data_quality === 'GO_AROUND' ? (
                <div style={{ background: '#bbf7e0', border: '1.5px solid #10b981', borderRadius: 8,
                  padding: '4px 6px', fontSize: '0.65rem', fontWeight: 700, color: '#065f46',
                  letterSpacing: '0.03em', textAlign: 'center', minWidth: 36, lineHeight: 1.3 }}>
                  ↥ GA
                </div>
              ) : f.data_quality === 'INSUFFICIENT_DATA' ? (
                <div className="grade-badge" style={{ background: '#e5e7eb', color: '#6b7280' }}>
                  NS
                </div>
              ) : (
                <div className={`grade-badge ${gradeClass(f.grade)}`} style={{ fontSize: '0.85rem' }}>
                  {f.percentage != null ? f.percentage : (f.grade || '?')}
                </div>
              )}
              <div className="score-info">
                <div className="score-callsign" style={f.callsign_masked ? { fontStyle: 'italic' } : {}}>{f.callsign_masked ? `✈ ${f.callsign}` : f.callsign}</div>
                <div className="score-meta">
                  <span>{f.arr_airport}</span>
                  {f.runway_id && <><span className="dot" /><span>RWY {f.runway_id}</span></>}
                  <span className="dot" /><span>{f.ac_type}</span>
                  {isVfr
                    ? <><span className="dot" /><span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#10b981', padding: '1px 6px', borderRadius: 4, border: '1.5px solid #10b981', background: '#bbf7e0', whiteSpace: 'nowrap' }}>Src: FA</span></>
                    : <><span className="dot" /><span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--accent)', padding: '1px 6px', borderRadius: 4, border: '1.5px solid #3b82f6', background: '#bfdbfe', whiteSpace: 'nowrap' }}>Src: FAA</span></>
                  }
                  {f.review_state === 'quarantined' && <><span className="dot" /><span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#a16207', padding: '1px 6px', borderRadius: 4, border: '1.5px solid #eab308', background: '#fef9c3', whiteSpace: 'nowrap' }}>⏳ Under review</span></>}
                  {f.pending && <><span className="dot" /><span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#1d4ed8', padding: '1px 6px', borderRadius: 4, border: '1.5px solid #3b82f6', background: '#dbeafe', whiteSpace: 'nowrap' }}>⏳ Processing — a few min</span></>}
                </div>
                <div className="score-meta">
                  <span>{formatFlightTime(f)}</span>
                  {_flightDisplayMode() !== 'legs' && f.landing_count > 1 && <><span className="dot" /><span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#7c3aed', padding: '1px 6px', borderRadius: 4, border: '1.5px solid #8b5cf6', background: '#ede9fe', whiteSpace: 'nowrap' }}>🛬 {f.landing_count} landings</span></>}
                  {f.wind_speed_kt > 0 && <><span className="dot" /><span>{f.wind_speed_kt}kt{f.wind_gust_kt > 0 ? ` G${f.wind_gust_kt}` : ''}</span></>}
                  {(f.landing_time || f.flight_date) && <span style={{ marginLeft: "auto", color: "var(--text-muted)", fontSize: "0.82rem", whiteSpace: "nowrap" }}>{timeAgo(f.landing_time || (f.flight_date + "T12:00:00Z"))}</span>}
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                {skillIcons.slice(0, 4).map((ic, i) => (
                  <span key={i} style={{ background: '#334155', border: '1px solid #1e293b',
                    borderRadius: 6, padding: '1px 5px', fontSize: 15, lineHeight: 1.35 }}>{renderSkillIcon(ic)}</span>
                ))}
                {skillIcons.length > 4 && (
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{skillIcons.length - 4}</span>
                )}
              </div>
            </div>
            {_flightDisplayMode() === 'legs' && (f.legs || []).length > 1 && (
              <div style={{ margin: '2px 0 12px 44px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {f.legs.map((l, li) => (
                  <div key={li} onClick={() => { if (!f.pending) navigate(`/flight/${f.id || encodeURIComponent(f.gufi)}`); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', borderRadius: 8,
                      background: 'var(--bg-card)', border: '1px solid var(--border)', cursor: 'pointer' }}>
                    <div className={`grade-badge ${l.grade ? gradeClass(l.grade) : ''}`}
                      style={{ minWidth: 30, fontSize: '0.7rem', padding: '3px 5px', ...(l.grade ? {} : { background: '#e5e7eb', color: '#6b7280' }) }}>
                      {l.percentage != null ? l.percentage : (l.grade || 'NS')}
                    </div>
                    <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', fontWeight: 600 }}>Landing {li + 1}</span>
                    {l.runway_id && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>RWY {l.runway_id}</span>}
                  </div>
                ))}
              </div>
            )}
            </div>
            );
          })}
          {hasMore && (
            <button onClick={loadMore} disabled={loadingMore}
              style={{ width: '100%', padding: 12, marginTop: 8, background: 'var(--bg-card)', color: 'var(--accent)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 16, fontWeight: 600, cursor: 'pointer', opacity: loadingMore ? 0.6 : 1 }}>
              {loadingMore ? 'Loading...' : `Load More (${results.length} shown)`}
            </button>
          )}
        </div>
      )}

      {showFeedExplainer && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          onClick={e => { if (e.target === e.currentTarget) setShowFeedExplainer(false); }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.65)' }} onClick={() => setShowFeedExplainer(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-card)', borderRadius: '20px 20px 0 0', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 -8px 40px rgba(0,0,0,0.5)', animation: 'slideUp 0.22s ease-out' }}>
            <div style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', padding: '14px 20px 12px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1 }}>
              <div style={{ width: 36, height: 4, background: '#555', borderRadius: 2, position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)' }} />
              <div style={{ fontSize: 19, fontWeight: 800, color: 'var(--text-primary)', marginTop: 8 }}>🛬 How the Flights feed works</div>
              <button onClick={() => setShowFeedExplainer(false)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 22, cursor: 'pointer', marginTop: 8 }}>×</button>
            </div>
            <div style={{ padding: '20px 20px 40px', display: 'flex', flexDirection: 'column', gap: 18 }}>

              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>What you're seeing</div>
                <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>✈</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>My Aircraft</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Approaches flown in tail numbers you've claimed in <em>Settings → My Aircraft</em> — including legs flown by other pilots of that aircraft.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>✓</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>My Flights</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Flights you've claimed as your own — tapped <em>Claim This Flight</em>, or auto-claimed from your tails. This is the set your proficiency tracking is based on.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>⊞</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Type Feed</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>Approaches across the fleet flown in the aircraft <em>types</em> on your pilot profile (e.g. SR22, C172). Useful for benchmarking against pilots flying the same equipment.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>👤</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Signed out</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>A sample of recent GA IFR arrivals with tail numbers masked. Sign in to filter to your own flights.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>How approaches get here</div>
                <div style={{ background: 'var(--bg-body)', borderRadius: 12, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>📡</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>SWIM feed (automatic)</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>WILCO ingests the FAA's SWIM stream 24/7 and scores any flight assigned a discrete transponder code within minutes of touchdown — no action required. SWIM only sees flights with a discrete code, so most VFR flying (squawking 1200) and a few others won't show up automatically — pull them in via <em>Manual retrieve</em> below.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>🔗</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Auto-claim</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>When SWIM scores an approach in a tail you've added to <em>My Aircraft</em>, it's automatically linked to your account and shows up in <strong>My Aircraft</strong>. Add your tails once and your flights flow in on their own.</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <span style={{ fontSize: 20, lineHeight: 1.2 }}>📥</span>
                    <div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Manual retrieve</div>
                      <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 }}>VFR approaches and any IFR flight that didn't appear in SWIM can be pulled in on-demand. Type a callsign in the search bar — if it's not found, tap <strong>"Retrieve it here"</strong> to fetch the track from FlightAware and score it.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ background: 'rgba(59,130,246,0.08)', borderRadius: 12, padding: 14, border: '1px solid rgba(59,130,246,0.2)' }}>
                <div style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  <strong style={{ color: 'var(--accent)' }}>Tip:</strong> Most of your IFR flying will land in the feed automatically. Use <em>Manual retrieve</em> for VFR pattern work, practice approaches not on an IFR plan, or older flights you'd like backfilled.
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Skills / Badges
// ---------------------------------------------------------------------------
export function SkillsBadges({ skills }) {
  if (!skills || skills.length === 0) return null;
  return (
    <div className="skills-section">
      <div className="section-title">Approach Skills</div>
      <div className="skills-grid">
        {skills.map((s, i) => (
          <div key={i} className="skill-badge" style={{ borderColor: s.color + '40' }}>
            <span className="skill-icon">{renderSkillIcon(s.icon)}</span>
            <div>
              <div className="skill-name" style={{ color: s.color }}>{s.name}</div>
              <div className="skill-value">{s.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Segment-Based Approach Debrief — tells the story of the approach by phase
// ---------------------------------------------------------------------------
function generateSegmentDebrief(flight, scores, categories) {
  const f = flight;
  const metrics = f.score_details?.metrics || {};
  const wind = f.score_details?.wind || {};
  const aircraftData = f.score_details?.aircraftData || {};
  const targetSpeed = aircraftData.targetSpeed || 70;
  const segments = [];

  // Determine IFR vs VFR
  const flightCat = scores.stabilized?.metrics?.flightCategory || metrics.flightCategory || 'VFR';
  const isIFR = flightCat === 'IFR' || flightCat === 'LIFR';

  // --- Segment 1: Setup / Configuration (3-2nm) ---
  const setup = { phase: isIFR ? 'FAF / Configuration' : 'Configuration', range: isIFR ? 'FAF ~3nm' : '3-2nm', icon: isIFR ? '📡' : '⚙️', notes: [], grade: null };
  if (isIFR) {
    setup.notes.push(`${flightCat} conditions — instrument approach. Should be stabilized from FAF (~3nm).`);
  }
  const speedMetrics = scores.speedControl?.metrics || {};
  const outerStart = speedMetrics.outerSpeedStart;
  const outerEnd = speedMetrics.outerSpeedEnd;
  if (outerStart && outerEnd) {
    if (outerStart > targetSpeed + 30) {
      setup.notes.push(`Entered at ${outerStart}kt — still decelerating from pattern entry.`);
    } else if (outerStart > targetSpeed + 15) {
      setup.notes.push(`Entering at ${outerStart}kt, configuring toward ${targetSpeed}kt target.`);
    } else {
      setup.notes.push(`Good entry speed at ${outerStart}kt, close to ${targetSpeed}kt target.`);
    }
    if (outerEnd < outerStart) {
      setup.notes.push(`Smooth deceleration to ${outerEnd}kt — good energy management.`);
      setup.grade = 'good';
    } else {
      setup.notes.push(`Speed not trending down — still at ${outerEnd}kt approaching 1nm.`);
      setup.grade = 'caution';
    }
  }
  // GS at this phase
  const avgGsDev = metrics.avgGsDev;
  if (avgGsDev !== undefined) {
    if (Math.abs(avgGsDev) < 100) {
      setup.notes.push(`Tracking glidepath well (avg ${avgGsDev > 0 ? '+' : ''}${avgGsDev}ft).`);
      if (!setup.grade) setup.grade = 'good';
    } else if (avgGsDev > 100) {
      setup.notes.push(`Above glidepath by ${avgGsDev}ft on average — started high.`);
      setup.grade = 'caution';
    } else {
      setup.notes.push(`Below glidepath by ${Math.abs(avgGsDev)}ft — watch the PAPI/VASI.`);
      setup.grade = 'warning';
    }
  }
  if (setup.notes.length > 0) segments.push(setup);

  // --- Segment 2: Intermediate (2-1nm) ---
  const inter = { phase: 'Intermediate', range: '2-1nm', icon: '📐', notes: [], grade: null };
  const horizMetrics = scores.horizontal?.metrics || {};
  const avgCross = horizMetrics.avgCrosstrack;
  if (avgCross !== undefined) {
    if (avgCross < 100) {
      inter.notes.push(`Good centerline tracking (avg ${avgCross}ft).`);
      inter.grade = 'good';
    } else if (avgCross < 300) {
      inter.notes.push(`Centerline deviation averaging ${avgCross}ft — workable but room to tighten up.`);
      inter.grade = 'caution';
    } else {
      inter.notes.push(`Tracking ${avgCross}ft off centerline — significant drift.`);
      inter.grade = 'warning';
    }
  }
  const clCrossings = horizMetrics.clCrossings;
  if (clCrossings > 1) {
    inter.notes.push(`${clCrossings} centerline crossings detected — S-turning on approach.`);
    inter.grade = 'warning';
  }
  // Stabilized check
  const stabMetrics = scores.stabilized?.metrics || {};
  const stabDist = stabMetrics.stabilizedDist;
  const stabTarget = isIFR ? 3.0 : 1.0;
  if (stabDist !== undefined && stabDist >= stabTarget) {
    inter.notes.push(`Stabilized by ${stabDist.toFixed(1)}nm — ${isIFR ? 'good instrument approach discipline' : 'well configured'}.`);
    if (!inter.grade) inter.grade = 'good';
  } else if (isIFR && stabDist !== undefined && stabDist >= 1.0 && stabDist < stabTarget) {
    inter.notes.push(`Stabilized at ${stabDist.toFixed(1)}nm — late for an instrument approach. Should be configured from FAF (~3nm).`);
    inter.grade = 'caution';
  }
  if (inter.notes.length > 0) segments.push(inter);

  // --- Segment 3: Short Final (<1nm) ---
  const final_ = { phase: 'Short Final', range: '<1nm', icon: '🎯', notes: [], grade: null };
  const avgCloseSpeed = speedMetrics.avgCloseSpeed;
  if (avgCloseSpeed !== undefined) {
    const dev = Math.abs(avgCloseSpeed - targetSpeed);
    if (dev <= 7) {
      final_.notes.push(`On-speed at ${avgCloseSpeed}kt (target ${targetSpeed}kt) — well managed.`);
      final_.grade = 'good';
    } else if (dev <= 15) {
      final_.notes.push(`Close-in speed averaging ${avgCloseSpeed}kt vs ${targetSpeed}kt target — ${avgCloseSpeed > targetSpeed ? 'fast' : 'slow'}.`);
      final_.grade = 'caution';
    } else {
      final_.notes.push(`Speed ${avgCloseSpeed}kt — ${dev}kt ${avgCloseSpeed > targetSpeed ? 'fast' : 'slow'} at the critical phase.`);
      final_.grade = 'warning';
    }
  }
  // Stabilized late?
  const lateDist = isIFR ? 2.5 : 1.0;
  if (stabDist !== undefined && stabDist < lateDist && stabDist > 0) {
    if (isIFR) {
      final_.notes.push(`Stabilized at ${stabDist.toFixed(1)}nm — not configured at FAF. On an ILS/LPV you need to be on-speed, on-glidepath, and on-centerline by 3nm.`);
    } else {
      final_.notes.push(`Didn't stabilize until ${stabDist.toFixed(1)}nm — late. Aim for 1nm+.`);
    }
    final_.grade = 'warning';
  } else if (stabDist === 0 || (scores.stabilized?.score === 0 && scores.stabilized?.max > 0)) {
    final_.notes.push(`Never fully stabilized${isIFR ? ' — missed approach criteria met' : '. Consider going around'}.`);
    final_.grade = 'warning';
  }
  // Bank angle concerns
  const bankMetrics = scores.bankAngle?.metrics || {};
  if (bankMetrics.steepBankCount > 0) {
    final_.notes.push(`⚠️ ${bankMetrics.steepBankCount} steep bank event${bankMetrics.steepBankCount > 1 ? 's' : ''} below pattern altitude — spin risk.`);
    final_.grade = 'warning';
  }
  if (final_.notes.length > 0) segments.push(final_);

  // --- Segment 4: Threshold / Touchdown ---
  const landing = { phase: 'Over the Numbers', range: 'threshold', icon: '🛬', notes: [], grade: null };
  const thrAgl = metrics.thresholdAgl;
  const rwyLen = metrics.runwayLengthFt || f.runway_json?.length_ft;
  const landingMargin = metrics.landingMarginPct;
  if (thrAgl !== undefined) {
    if (thrAgl >= 35 && thrAgl <= 75) {
      landing.notes.push(`Crossed threshold at ${thrAgl}ft AGL — right on target.`);
      landing.grade = 'good';
    } else if (thrAgl > 75 && thrAgl <= 100) {
      landing.notes.push(`Threshold crossing ${thrAgl}ft — slightly high but acceptable.`);
      landing.grade = 'caution';
    } else if (thrAgl > 100) {
      landing.notes.push(`Crossed at ${thrAgl}ft AGL — high. This eats up runway and leads to floating.`);
      landing.grade = 'warning';
    } else if (thrAgl < 35) {
      landing.notes.push(`Threshold crossing ${thrAgl}ft — low! Obstacle clearance matters.`);
      landing.grade = 'warning';
    }
  }
  if (rwyLen && landingMargin != null) {
    const marginNote = landingMargin >= 50 ? '' : landingMargin >= 25 ? ' — adequate but watch your energy.' : ' — tight margin, consider going around.';
    landing.notes.push(`Runway ${Number(rwyLen).toLocaleString()}ft, est. ${landingMargin}% remaining${marginNote}`);
    if (landingMargin < 25 && (!landing.grade || landing.grade === 'good')) landing.grade = 'warning';
    else if (landingMargin < 50 && landing.grade === 'good') landing.grade = 'caution';
  }
  if (landing.notes.length > 0) segments.push(landing);

  // Fallback for old-format scores (VFR_FA_1.0 etc) — generate basic debrief from available data
  if (segments.length === 0) {
    const fallback = { phase: 'Approach Summary', range: 'overall', icon: '📋', notes: [], grade: null };
    const totalPct = f.percentage || 0;
    if (totalPct >= 90) { fallback.grade = 'good'; fallback.notes.push(`Strong approach — ${totalPct}% overall.`); }
    else if (totalPct >= 70) { fallback.grade = 'caution'; fallback.notes.push(`Acceptable approach — ${totalPct}% overall. Room for improvement.`); }
    else { fallback.grade = 'warning'; fallback.notes.push(`Below standard — ${totalPct}% overall. Review your technique.`); }

    // Pull what we can from categories
    const cats = categories || [];
    cats.forEach(c => {
      if (c.max > 0) {
        const pct = (c.score / c.max) * 100;
        if (pct < 70) {
          fallback.notes.push(`${c.name}: ${c.score}/${c.max} — needs work.`);
        }
      }
    });

    // Check deductions from score breakdown
    const allScores = scores || {};
    Object.keys(allScores).forEach(key => {
      const cat = allScores[key];
      if (cat?.deductions?.length > 0) {
        cat.deductions.forEach(d => {
          if (typeof d === 'string' && d.trim()) fallback.notes.push(d.replace(/^-\d+:\s*/, '⚠️ ').replace(/(\d+\.\d{4,})/g, n => Math.round(parseFloat(n))));
        });
      }
    });

    if (wind.speed > 0 && wind.crosswind > 5) {
      fallback.notes.push(`${wind.crosswind}kt crosswind — good practice conditions.`);
    }

    if (fallback.notes.length > 0) segments.push(fallback);
  }

  return segments;
}

function generateConditionsSummary(flight) {
  const wind = flight.score_details?.wind || {};
  const parts = [];
  if (wind.speed > 0) {
    let w = `${wind.speed}kt`;
    if (wind.gust > 0) w += ` G${wind.gust}`;
    if (wind.crosswind > 5) w += `, ${wind.crosswind}kt crosswind`;
    parts.push(wind.dir != null ? `Wind ${wind.dir}° at ${w}` : `Wind variable at ${w}`);
  } else {
    parts.push('Calm wind');
  }
  return parts.join('. ');
}

export function ApproachExplainer({ flight, scores, categories, skills, benchmark, rank, fleetTotal, acFamily }) {
  const [expanded, setExpanded] = useState(false);
  const [feedbackMode, setFeedbackMode] = useState(false);
  const [feedback, setFeedback] = useState({});
  const [feedbackSaved, setFeedbackSaved] = useState(false);
  const { token } = useAuth();
  const segments = generateSegmentDebrief(flight, scores, categories);
  const condSummary = generateConditionsSummary(flight);
  const flightCat = scores.stabilized?.metrics?.flightCategory || flight.score_details?.metrics?.flightCategory || 'VFR';
  const isIFR = flightCat === 'IFR' || flightCat === 'LIFR';

  const gradeDesc = { A: 'an excellent', B: 'a solid', C: 'an acceptable', D: 'a below-average', F: 'a poor' };
  const phaseColors = { good: '#22c55e', caution: '#eab308', warning: '#ef4444' };

  // Load existing feedback
  useEffect(() => {
    if (token && flight.id) {
      authFetch(`/api/score_feedback/${flight.id}`, token)
        .then(d => { if (d?.feedback) setFeedback(d.feedback); })
        .catch(() => {});
    }
  }, [flight.id, token]);

  const submitFeedback = async (category, rating) => {
    if (!token) return;
    const updated = { ...feedback, [category]: { rating, comment: feedback[category]?.comment || '' } };
    setFeedback(updated);
    try {
      await authPost('/api/score_feedback', {
        scored_approach_id: flight.id,
        category,
        rating,
        comment: updated[category].comment
      }, token);
      setFeedbackSaved(true);
      setTimeout(() => setFeedbackSaved(false), 2000);
    } catch (e) { console.error('Feedback error:', e); }
  };

  if (!segments.length) return null;

  const ratingButtons = ['too_harsh', 'fair', 'too_lenient'];
  const ratingLabels = { too_harsh: '📉 Too Harsh', fair: '✅ Fair', too_lenient: '📈 Too Easy' };
  const ratingColors = { too_harsh: '#3b82f6', fair: '#22c55e', too_lenient: '#f59e0b' };

  return (
    <div className="category-card explainer-card" style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }}>
      {/* Header + always-visible summary */}
      <div style={{ padding: '12px 14px 10px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <span style={{ fontSize: '0.88rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>📋</span> Approach Debrief
          </span>
        </div>
        {/* Always-visible summary line */}
        <div style={{ fontSize: '0.82rem', lineHeight: 1.5, color: 'var(--text-primary)' }}>
          This was {gradeDesc[flight.grade] || 'an'} {isIFR ? 'instrument' : 'visual'} approach into {flight.arr_airport}{flight.runway_id ? ` ${flight.runway_id}` : ''}.{' '}
          <span style={{ color: 'var(--text-secondary)' }}>{condSummary}</span>
          {' '}<span onClick={() => setExpanded(!expanded)} style={{ color: '#3b82f6', cursor: 'pointer', fontWeight: 600, whiteSpace: 'nowrap' }}>
            {expanded ? 'less ▲' : 'more ▼'}
          </span>
        </div>
      </div>
      {expanded && (
        <div style={{ padding: '0 14px 14px' }}>

          {/* Segment cards */}
          {segments.map((seg, i) => (
            <div key={i} style={{
              background: 'var(--card-bg, rgba(255,255,255,0.03))',
              borderRadius: 8,
              padding: '10px 12px',
              marginBottom: 8,
              borderLeft: `3px solid ${phaseColors[seg.grade] || '#64748b'}`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600 }}>{seg.icon} {seg.phase}</span>
                <span style={{ fontSize: '0.68rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{seg.range}</span>
              </div>
              {seg.notes.map((note, j) => (
                <div key={j} style={{ fontSize: '0.78rem', lineHeight: 1.45, color: 'var(--text-secondary)', paddingLeft: 2, marginBottom: 2 }}>
                  {note}
                </div>
              ))}
            </div>
          ))}

          {/* Fleet context */}
          {benchmark && rank && fleetTotal && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: 8 }}>
              {flight.percentage > parseFloat(benchmark.avg_percentage) + 5
                ? `${(flight.percentage - parseFloat(benchmark.avg_percentage)).toFixed(0)} points above the ${acFamily || flight.ac_type} fleet avg — ranked #${rank} of ${fleetTotal}.`
                : flight.percentage < parseFloat(benchmark.avg_percentage) - 5
                ? `${(parseFloat(benchmark.avg_percentage) - flight.percentage).toFixed(0)} points below the ${acFamily || flight.ac_type} fleet avg — ranked #${rank} of ${fleetTotal}.`
                : `Right in line with the ${flight.ac_type} fleet average — ranked #${rank} of ${fleetTotal}.`}
            </div>
          )}

          {/* Feedback toggle */}
          {token && (
            <div style={{ marginTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button onClick={(e) => { e.stopPropagation(); setFeedbackMode(!feedbackMode); }}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 6, padding: '4px 10px', fontSize: '0.72rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  {feedbackMode ? '✕ Close' : '💬 Rate This Score'}
                </button>
                {feedbackSaved && <span style={{ fontSize: '0.7rem', color: '#22c55e' }}>✓ Saved</span>}
              </div>
              {feedbackMode && (
                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginBottom: 8 }}>How does this score feel?</div>
                  {/* Overall rating */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: '0.75rem', fontWeight: 600, marginBottom: 4 }}>Overall</div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {ratingButtons.map(r => (
                        <button key={r} onClick={(e) => { e.stopPropagation(); submitFeedback('overall', r); }}
                          style={{
                            flex: 1, padding: '6px 0', borderRadius: 6, fontSize: '0.68rem', cursor: 'pointer',
                            border: feedback.overall?.rating === r ? `2px solid ${ratingColors[r]}` : '1px solid rgba(255,255,255,0.12)',
                            background: feedback.overall?.rating === r ? `${ratingColors[r]}22` : 'transparent',
                            color: feedback.overall?.rating === r ? ratingColors[r] : 'var(--text-secondary)',
                          }}>
                          {ratingLabels[r]}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Per-category ratings */}
                  {categories.filter(ct => ct.max > 0).map(ct => (
                    <div key={ct.key} style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 500, marginBottom: 3, color: 'var(--text-secondary)' }}>{ct.name} ({ct.score}/{ct.max})</div>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {ratingButtons.map(r => (
                          <button key={r} onClick={(e) => { e.stopPropagation(); submitFeedback(ct.key, r); }}
                            style={{
                              flex: 1, padding: '4px 0', borderRadius: 5, fontSize: '0.62rem', cursor: 'pointer',
                              border: feedback[ct.key]?.rating === r ? `2px solid ${ratingColors[r]}` : '1px solid rgba(255,255,255,0.08)',
                              background: feedback[ct.key]?.rating === r ? `${ratingColors[r]}18` : 'transparent',
                              color: feedback[ct.key]?.rating === r ? ratingColors[r] : 'var(--text-muted)',
                            }}>
                            {r === 'too_harsh' ? '📉 Harsh' : r === 'fair' ? '✅ Fair' : '📈 Easy'}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { HomePage };
export default HomePage;
